"use client";

import { DragOverlay } from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  snapCenterToCursor,
} from "@dnd-kit/modifiers";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DragOverlayPortalProps {
  children: React.ReactNode;
  activeId: string | null;
  xOffset?: number;
}

/**
 * DragOverlayをポータル経由でレンダリング
 * ScrollArea制約を回避して正しく表示
 */
export const DragOverlayPortal = function DragOverlayPortal({
  children,
  activeId,
  xOffset = -4,
}: DragOverlayPortalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isPortalReady, setIsPortalReady] = useState(false);

  // ポータルコンテナを作成・管理
  useEffect(() => {
    if (activeId && !containerRef.current) {
      const container = document.createElement("div");
      container.className =
        "fixed inset-0 pointer-events-none z-[9999] cursor-grabbing [&_li]:list-none";

      // グローバルカーソル設定
      const globalStyle = document.createElement("style");
      globalStyle.textContent = `
        body, body * {
          cursor: grabbing !important;
        }
      `;
      container.appendChild(globalStyle);

      document.body.appendChild(container);
      containerRef.current = container;
      setIsPortalReady(true);
    } else if (!activeId && containerRef.current) {
      document.body.removeChild(containerRef.current);
      containerRef.current = null;
      setIsPortalReady(false);
    }
  }, [activeId]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        document.body.removeChild(containerRef.current);
        containerRef.current = null;
        setIsPortalReady(false);
      }
    };
  }, []);

  // ドラッグモディファイアをメモ化
  const dragModifiers = useMemo(
    () => [
      snapCenterToCursor,
      restrictToVerticalAxis,
      restrictToWindowEdges,
      // X軸のオフセット調整
      (args: {
        transform: { x: number; y: number; scaleX: number; scaleY: number };
      }) => {
        const { transform } = args;
        return {
          ...transform,
          x: transform.x + xOffset,
          y: transform.y,
        };
      },
    ],
    [xOffset],
  );

  // activeIdとポータルが準備できた場合のみ作成
  if (!activeId || !isPortalReady || !containerRef.current) {
    return null;
  }

  return createPortal(
    <DragOverlay
      modifiers={dragModifiers}
      dropAnimation={null}
      adjustScale={false}
    >
      {children}
    </DragOverlay>,
    containerRef.current,
  );
};
